/**
 * Šta operater vidi kad zahtev ne prođe.
 *
 * **Zašto ovo postoji odvojeno.** Izmereno 23.09.2026: od 47 odgovora `400` u
 * API-ju, **45** nosi rečenicu napisanu za čoveka — „Odbijanje mora nositi
 * razlog.", „Koordinata je van opsega Srbije — proveri pin pre potvrde." Sve su
 * dolazile do ekrana obložene tehničkim prefiksom i pod navodnicima:
 *
 * ```
 * Zahtev /api/corridors/5 nije uspeo (400). "Pravac mora imati naziv."
 * ```
 *
 * Time je pažljivo napisana poruka izgledala kao ispad programa, pa je čovek
 * tražio grešku tamo gde je nije bilo. Prefiks ima smisla za 500 — tamo poruke
 * za čoveka i nema — ali ne za 400, koji upravo i znači „ovo je na tebi".
 *
 * Odvojeno od `api.ts` da bi se moglo ispitati bez mreže: oblik tela odgovora je
 * dogovor sa serverom, a dogovori se lome nečujno.
 */

/** Oblici tela koje server ume da pošalje uz `400`. */
interface TeloGreske {
  /** `BadRequest(new { blockers = ... })` — brane pri čuvanju ture. */
  blockers?: unknown;

  /** Provera modela iz ASP.NET-a: `{ errors: { polje: [poruke] } }`. */
  errors?: Record<string, unknown>;

  /** `ProblemDetails`. */
  title?: unknown;
  detail?: unknown;
}

function tekstoviIz(vrednost: unknown): string[] {
  if (typeof vrednost === 'string') {
    return vrednost.trim().length > 0 ? [vrednost.trim()] : [];
  }

  if (Array.isArray(vrednost)) {
    return vrednost.flatMap(tekstoviIz);
  }

  if (vrednost !== null && typeof vrednost === 'object') {
    return Object.values(vrednost as Record<string, unknown>).flatMap(tekstoviIz);
  }

  return [];
}

/**
 * Poruka za čoveka iz tela odgovora, ili `null` kad je telo tehničko.
 *
 * `null` je namerno razlikovan od prazne niske: znači „ovde nema šta da se
 * pročita", pa pozivalac sme da doda svoj opis.
 */
export function porukaIzTela(telo: string): string | null {
  const ocisceno = telo.trim();

  if (ocisceno.length === 0) {
    return null;
  }

  let razlozeno: unknown;

  try {
    razlozeno = JSON.parse(ocisceno);
  } catch {
    // Nije JSON. HTML stranica greške nije poruka za čoveka, a običan tekst je.
    return ocisceno.startsWith('<') ? null : ocisceno;
  }

  if (typeof razlozeno === 'string') {
    return razlozeno.trim().length > 0 ? razlozeno.trim() : null;
  }

  if (razlozeno === null || typeof razlozeno !== 'object') {
    return null;
  }

  const telo2 = razlozeno as TeloGreske;

  // Brane se nabrajaju sve: operater mora videti svaku, ne prvu.
  const brane = tekstoviIz(telo2.blockers);
  if (brane.length > 0) {
    return brane.join(' ');
  }

  const provere = tekstoviIz(telo2.errors);
  if (provere.length > 0) {
    return provere.join(' ');
  }

  const naslov = tekstoviIz(telo2.title);
  if (naslov.length > 0) {
    const dopuna = tekstoviIz(telo2.detail);
    return dopuna.length > 0 ? `${naslov[0]} ${dopuna[0]}` : naslov[0];
  }

  return null;
}

/**
 * Opis neuspelog zahteva, onako kako ga operater treba pročitati.
 *
 * @param status HTTP kod.
 * @param telo Telo odgovora kao tekst; prazno kad ga nema.
 * @param path Putanja, samo za tehničke greške — čoveku ne znači ništa.
 */
export function opisiGresku(status: number, telo: string, path: string): string {
  // NA PRIJAVI 401 i 503 ne znače „istekla ti je prijava" ni „ERP ne radi" —
  // server uz njih piše zašto prijava nije prošla (pogrešna lozinka, nalog bez
  // objekta, vozač ne ulazi ovim putem). Analiza 2.0, 14.10.2026: čovek koji je
  // pogrešio lozinku čitao je „Prijava je istekla".
  if (path.includes('/api/auth/login') && (status === 401 || status === 503)) {
    const razlog = porukaIzTela(telo);
    if (razlog !== null) {
      return razlog;
    }

    return status === 401
      ? 'Prijava nije prošla — proveri ime i lozinku.'
      : 'Server trenutno ne može da proveri prijavu. Pokušaj ponovo za minut.';
  }

  if (status === 401) {
    return 'Prijava je istekla. Osveži stranicu i prijavi se ponovo.';
  }

  if (status === 403) {
    return 'Ovaj objekat nije tvoj — poslovođa vidi samo svoju prodaju.';
  }

  if (status === 503) {
    return 'ERP baza trenutno nije dostupna. Podaci koji se vide mogu biti zastareli.';
  }

  const poruka = porukaIzTela(telo);

  // 400 i 409 znače „ovo je na tebi" i server uz njih piše rečenicu. Ona ide
  // sama, bez prefiksa — inače izgleda kao ispad programa.
  if (poruka !== null && (status === 400 || status === 409)) {
    return poruka;
  }

  // Sve ostalo je tehničko: putanja i kod pomažu onome ko to gleda u dnevniku.
  return poruka === null
    ? `Zahtev ${path} nije uspeo (${status}).`
    : `Zahtev ${path} nije uspeo (${status}). ${poruka}`;
}

/**
 * Baca grešku sa rečenicom napisanom za čoveka, kad zahtev ne prođe.
 *
 * **Zašto postoji.** Operaterov ekran ide kroz `api.ts` i time kroz
 * `opisiGresku`. Poslovođin ekran ne ide — on zove `fetch` direktno, pa je do
 * 23.09.2026. na pet mesta stajalo `throw new Error(await response.text())`.
 * Poslovođa je zbog toga video poruku pod navodnicima, a kod 500 celu IIS
 * stranicu greške na telefonu.
 *
 * Izmereno tog dana: dva kontrolera koja taj ekran zove nose **16** poruka
 * napisanih za čoveka („Izaberi ko vozi.", „Neka od izabranih isporuka više nije
 * na spisku. Osveži ekran.") — sve su dolazile obložene.
 */
export async function proveriOdgovor(response: Response, path: string): Promise<void> {
  if (response.ok) {
    return;
  }

  const telo = await response.text().catch(() => '');

  throw new Error(opisiGresku(response.status, telo, path));
}
